import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslate } from 'react-redux-multilingual/lib/context'
import moment from 'moment'
import ActionBar from '../../../../components/ActionBar/ActionBar'
import FilterModal from '../../../../components/FilterModal/FilterModal'
import PageInfo from '../../../../components/PageInfo/PageInfo'
import PleaseFilter from '../../../../components/PleaseFilter/PleaseFilter'
import {
  applyFilterData,
  extractFilterOptions,
  updateFilterOptions,
  updateObject,
  updateFilterData
} from '../../../../helpers'
import {
  fetchAppraisalYearOptions,
  fetchForm,
  fetchOrganizationOptions
} from '../../../../store/actions'
import { Card, CardContent, CardHeader, Grid, Typography } from '@material-ui/core'
import DataTable from '../../../../components/DataTable/DataTable'
import { fetchFeedbackReportData } from '../../../../store/actions/report/feedbackReport'
import Alert from '../../../../components/Alert/Alert'

const optionsMapper = {
  form_id: {
    valueKey: 'form_id',
    labelKey: 'form_name'
  },
  appraisal_year: {
    valueKey: 'year_id',
    labelKey: 'year_name'
  },
  org_id: {
    valueKey: 'org_id',
    labelKey: { name: 'org_name', code: 'org_code' }
  }
}

function FeedbackReport() {
  const dispatch = useDispatch()
  const translate = useTranslate()

  const yearOptions = useSelector(state => state.feedbackReport.years)
  const formOptions = useSelector(state => state.feedbackReport.forms)
  const orgOptions = useSelector(state => state.feedbackReport.orgs)
  const loadingStates = useSelector(state => state.feedbackReport.loadingStates)
  const currentFilter = useSelector(state => state.feedbackReport.currentFilter)
  const currentForm = useSelector(state => state.feedbackReport.currentForm)
  const records = useSelector(state => state.feedbackReport.records)
  const pagination = useSelector(state => state.feedbackReport.pagination)

  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filterControls, setFilterControls] = useState({
    appraisal_year: {
      label: translate('appraisalYear'),
      value: moment().year(),
      configs: { options: [] },
      required: true
    },
    form_id: {
      label: translate('form'),
      value: '',
      configs: { options: [] },
      required: true
    },
    org_id: {
      label: translate('organization'),
      value: '',
      configs: { options: [] },
      required: true
    }
  })

  const updateFilterItemOptions = useCallback((formKey, newOptions) => {
    setFilterControls(controls => {
      const filterItem = controls[formKey]
      const mapper = optionsMapper[formKey]

      let updatedFilter = updateFilterOptions(
        newOptions,
        filterItem,
        mapper.valueKey,
        mapper.valueKey,
        mapper.labelKey
      )

      if (formKey !== 'appraisal_year') {
        if (formKey !== 'org_id') {
          updatedFilter = { ...updatedFilter, value: updatedFilter.configs.options[0] ?? null }
        }
      } else {
        updatedFilter = {
          ...updatedFilter,
          value: (updatedFilter.configs?.options ?? []).find(
            item => item.value === moment().year() ?? null
          )
        }
      }

      return updateObject(controls, { [formKey]: updatedFilter })
    })
  }, [])

  const filterClickedHandler = useCallback(() => {
    setFilterModalOpen(true)
  }, [])

  const filterChangedHandler = useCallback((filterKey, event) => {
    setFilterControls(controls => {
      const filterItem = controls[filterKey]
      const updatedFilter = updateFilterData(filterItem, event)
      return updateObject(controls, { [filterKey]: updatedFilter })
    })
  }, [])

  const filterCancelledHandler = useCallback(() => setFilterModalOpen(false), [])

  const filterSavedHandler = useCallback(() => {
    const paginationPayload = { page: pagination.currentPage, perPage: pagination.perPage }
    const data = extractFilterOptions(filterControls, { ...paginationPayload })
    dispatch(fetchFeedbackReportData(data))
    filterCancelledHandler()
  }, [dispatch, pagination.perPage, pagination.currentPage, filterControls, filterCancelledHandler])

  const onPageChangeHandler = useCallback(
    (event, page) => {
      const pageOptions = {
        page: page,
        perPage: pagination.perPage
      }
      const payload = extractFilterOptions(filterControls, pageOptions)
      dispatch(fetchFeedbackReportData(payload))
    },
    [dispatch, filterControls, pagination]
  )

  const onPerPageChangeHandler = useCallback(
    perPage => {
      const pageOptions = {
        page: pagination.currentPage,
        perPage: perPage
      }
      const payload = extractFilterOptions(filterControls, pageOptions)
      dispatch(fetchFeedbackReportData(payload))
    },
    [dispatch, filterControls, pagination]
  )

  const tableStructure = useMemo(
    () => [
      {
        field: 'index',
        title: '#',
        width: 70
      },
      {
        field: 'emp_code',
        title: translate('empCode'),
        type: 'string',
        width: 150,
        render: rowData => rowData.emp.emp_code
      },
      {
        field: 'emp_name',
        title: translate('employeeName'),
        type: 'string',
        render: rowData => rowData.emp.emp_name
      },
      {
        field: 'level_name',
        title: translate('level'),
        type: 'string',
        render: rowData => rowData.level.level_name
      },
      {
        field: 'position_name',
        title: translate('position'),
        type: 'string',
        render: rowData => rowData.position.position_name
      },
      {
        field: 'item_name',
        title: translate('appraisalItem'),
        render: rowData =>
          rowData.items.map(item => (
            <Typography key={item.item_id} variant={'body2'}>
              {item.item_code} - {item.item_name}
            </Typography>
          ))
      }
    ],
    [translate]
  )

  // Fetch parameter on mount
  useEffect(() => {
    dispatch(fetchAppraisalYearOptions())
    dispatch(fetchOrganizationOptions())
    return () => {}
  }, [dispatch])

  // Update filter options
  useEffect(() => {
    updateFilterItemOptions('form_id', formOptions)
  }, [formOptions, updateFilterItemOptions])

  useEffect(() => {
    updateFilterItemOptions('appraisal_year', yearOptions)
  }, [yearOptions, updateFilterItemOptions])

  useEffect(() => {
    updateFilterItemOptions('org_id', orgOptions)
  }, [orgOptions, updateFilterItemOptions])

  // Filter Controls sync
  useEffect(() => {
    if (currentFilter) {
      setFilterControls(controls => {
        return applyFilterData(controls, currentFilter)
      })
    }
  }, [currentFilter])

  // Cascaded filter options
  useEffect(() => {
    const filterOptions = {
      appraisal_year: filterControls.appraisal_year.value?.value ?? null
    }
    dispatch(fetchForm(filterOptions))
  }, [dispatch, filterControls.appraisal_year.value])

  let dateDisplay = null
  if (currentForm) {
    const { form_name, appraisal_year, start_date, end_date } = currentForm
    const sysDateFormat = 'YYYY-MM-DD'
    const displayDateFormat = 'DD MMMM YYYY'
    dateDisplay = (
      <Typography variant={'body2'}>
        {form_name} | {appraisal_year} |{' '}
        {moment(start_date, sysDateFormat).format(displayDateFormat)} -{' '}
        {moment(end_date, sysDateFormat).format(displayDateFormat)}
      </Typography>
    )
  }

  const tableTitle = (
    <Grid container alignItems={'flex-start'} direction={'column'}>
      <Grid item>
        <Typography variant={'h6'}>{translate('organization')}: {filterControls.org_id.value?.label ?? ''}</Typography>
      </Grid>
      <Grid item>{dateDisplay}</Grid>
    </Grid>
  )

  return (
    <>
      <FilterModal
        open={filterModalOpen}
        filterControls={filterControls}
        filterChanged={filterChangedHandler}
        cancelled={filterCancelledHandler}
        filterSaved={filterSavedHandler}
        loadingQueue={loadingStates}
      />
      <Alert />
      <Card>
        <CardHeader
          title={
            <ActionBar
              leftContent={<PageInfo pageTranslateKey={'feedbackReport'} />}
              filtering={filterClickedHandler}
              show={['filter']}
            />
          }
        />
        <CardContent>
          <PleaseFilter show={!currentFilter} noData={!records.length}>
            <DataTable
              tableTitle={tableTitle}
              loading={loadingStates.includes('records')}
              data={records}
              structure={tableStructure}
              pagination={pagination}
              pageChange={onPageChangeHandler}
              perPageChange={onPerPageChangeHandler}
            />
          </PleaseFilter>
        </CardContent>
      </Card>
    </>
  )
}

export default FeedbackReport
